import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { company } from "../data/companyData";
import { productsData } from "../data/productsData";
import "./styles/CustomerSupportChatbot.css";

const ORDER_INTENT = [
  "buy", "purchase", "order", "want to buy", "interested in buying",
  "how much", "price", "cost", "quote", "checkout", "payment", "rate",
];

const QUICK_PROMPTS = [
  "What do you stock for fertilizer blending?",
  "Purity and packing for potassium carbonate?",
  "Do you ship outside Maharashtra?",
];

const GREETING = {
  role: "assistant",
  content: `Hi, I'm the ${company.shortName} assistant. Ask me about any compound we stock, its grade and packing, or how to place an order.`,
  at: new Date(),
};

/**
 * Groq-backed support assistant.
 *
 * The product/contact knowledge is generated from productsData and
 * companyData, so it cannot drift from the catalogue the way a hand-written
 * block would. REACT_APP_GROQ_API_KEY is inlined into the client bundle by
 * CRA — keep that key low-privilege.
 */
const CustomerSupportChatbot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const knowledge = useMemo(() => {
    const catalogue = productsData
      .map(
        (p) =>
          `- ${p.name} (${p.formula}): ${p.grade}, purity ${p.purity}, ${p.packaging}. Category: ${p.category}. Applications: ${p.applications.join(", ")}.`
      )
      .join("\n");

    return `Company: ${company.name} (${company.business}), ${company.address.city}, established ${company.established}.
GST: ${company.gst}

Catalogue (the only products we stock):
${catalogue}

Contact:
- Office: ${company.phones.office.display}
- WhatsApp: ${company.phones.whatsapp.display}
- Landline: ${company.phones.landline.display}
- Email: ${company.email}
- Address: ${company.address.full}

Commercial terms:
- Dispatch: ${company.shipping.join(", ")}
- Payment: ${company.payments.join(", ")}
- Certificate of analysis supplied with every consignment
- Office hours: ${company.hours.map((h) => `${h.days} ${h.time}`).join("; ")}
- Custom procurement available for compounds outside the catalogue`;
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("no-scroll");
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
      cancelAnimationFrame(id);
    };
  }, [isOpen, onClose]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;

    const history = [...messages, { role: "user", content, at: new Date() }];
    setMessages(history);
    setInput("");
    setLoading(true);

    const hasOrderIntent = ORDER_INTENT.some((k) => content.toLowerCase().includes(k));

    const systemPrompt = `You are the customer support and sales assistant for ${company.name}, an industrial chemical importer and wholesaler. Answer only from the information below.

${knowledge}

Guidelines:
1. Be concise, professional and specific. Two short paragraphs maximum.
2. Recommend products from the catalogue only. Never invent a product or specification.
3. Quote purity and packing exactly as listed. Never state a price or rate: every product is priced on request, so direct pricing questions to a quote via the contact page, phone or WhatsApp.
4. If asked for something outside the catalogue, offer our custom procurement service.
5. If you do not know, say so and point to the contact channels.
6. Write in plain sentences. Never use em dashes or en dashes; use commas, full stops or colons instead.
${hasOrderIntent ? "7. The customer is showing buying intent, so after answering, direct them to the contact channels to place the order." : ""}`;

    try {
      const apiKey = process.env.REACT_APP_GROQ_API_KEY;
      if (!apiKey) throw new Error("Missing REACT_APP_GROQ_API_KEY");

      const response = await fetch("https://api.groq.com/openai/v1/chat/completions", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "llama3-8b-8192",
          messages: [
            { role: "system", content: systemPrompt },
            // Send recent turns so follow-up questions keep their context
            ...history.slice(-8).map(({ role, content: c }) => ({ role, content: c })),
          ],
          max_tokens: 800,
          temperature: 0.6,
        }),
      });

      if (!response.ok) throw new Error(`Groq responded ${response.status}`);

      const data = await response.json();
      let reply = data.choices?.[0]?.message?.content?.trim();
      if (!reply) throw new Error("Empty completion");

      if (hasOrderIntent) {
        reply += `\n\nTo place the order: call ${company.phones.office.display}, WhatsApp ${company.phones.whatsapp.display}, or email ${company.email}.`;
      }

      setMessages((prev) => [...prev, { role: "assistant", content: reply, at: new Date() }]);
    } catch (error) {
      console.error("Chatbot error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `I can't reach the assistant right now. Please call ${company.phones.office.display}, WhatsApp ${company.phones.whatsapp.display} or email ${company.email}. Our team will help straight away.`,
          at: new Date(),
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const time = (d) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return createPortal(
    <div className="cbot" role="dialog" aria-modal="true" aria-label="AI customer support">
      <button
        type="button"
        className="cbot__scrim"
        onClick={onClose}
        aria-label="Close assistant"
        tabIndex={-1}
      />

      <div className="cbot__panel">
        <header className="cbot__head">
          <span className="cbot__avatar" aria-hidden="true">
            <i className="fas fa-comment-dots" />
          </span>
          <span className="cbot__id">
            <strong>{company.shortName} assistant</strong>
            <span>
              <i className="cbot__dot" aria-hidden="true" /> Answers from our live catalogue
            </span>
          </span>
          <button type="button" className="cbot__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="cbot__log">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`cmsg cmsg--${m.role}${m.error ? " cmsg--error" : ""}`}>
              <div className="cmsg__bubble">
                {m.content.split("\n").map((line, j) =>
                  line ? <p key={j}>{line}</p> : <br key={j} />
                )}
              </div>
              <span className="cmsg__time mono">{time(m.at)}</span>
            </div>
          ))}

          {loading && (
            <div className="cmsg cmsg--assistant">
              <div className="cmsg__bubble cmsg__bubble--typing">
                <span /><span /><span />
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>

        {messages.length === 1 && !loading && (
          <div className="cbot__prompts">
            {QUICK_PROMPTS.map((q) => (
              <button key={q} type="button" onClick={() => send(q)}>
                {q}
              </button>
            ))}
          </div>
        )}

        <form
          className="cbot__composer"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about a compound, grade or price…"
            aria-label="Message"
            disabled={loading}
          />
          <button
            type="submit"
            aria-label="Send message"
            disabled={loading || !input.trim()}>
            <i className="fas fa-arrow-up" aria-hidden="true" />
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CustomerSupportChatbot;
